import { toFacetFilterOptions } from "@/components";
import { useAutoApplyFilters } from "@/hooks/useAutoApplyFilters";
import { useFacets } from "@/hooks/useFacetFilters";
import { PAYMENTS_FACET_SOURCE } from "@/services";
import { currentUserAtom } from "@/store/login";
import { getPaymentStateTranslation } from "@/utils/translations";
import {
	type FieldConfig,
	type FilterConfig,
	GenericFilters,
} from "@st-marketplace/ui";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PAYMENT_RECEIVED_SEARCH_CONSTANTS } from "./paymentReceivedSearch.constant";
import type { PaymentReceivedSearchProps } from "./paymentReceivedSearch.types";

const { schema } = PAYMENT_RECEIVED_SEARCH_CONSTANTS;

type SchemaType = {
	payment_type: string;
	username: string;
	statuses: string;
};

function PaymentReceivedSearch({
	onSearch,
	filters,
}: PaymentReceivedSearchProps) {
	const { t } = useTranslation();
	const user = useAtomValue(currentUserAtom);

	const {
		filters: localFilters,
		onFiltersChange,
		clearFilters,
	} = useAutoApplyFilters<SchemaType>({
		initialFilters: {
			payment_type: filters.payment_type ?? "",
			username: filters.username ?? "",
			statuses: filters.statuses ?? "",
		},
		onApply: (next) =>
			onSearch(
				schema.parse({
					payment_type: next.payment_type || undefined,
					username: next.username || undefined,
					statuses: next.statuses || undefined,
				}),
			),
		textFilterKeys: ["username"],
	});

	const facetsCounts = useFacets({
		source: PAYMENTS_FACET_SOURCE,
		scope: { payment_direction: "received", user_id: user?.id },
		filters: {
			statuses: filters.statuses ? [filters.statuses] : null,
			payment_type: filters.payment_type,
			buyer_display_name: filters.username,
		},
		ownParams: { payment_types: "payment_type" },
	});

	const statusOptions = useMemo(
		() =>
			toFacetFilterOptions(
				facetsCounts.statuses,
				(status) => getPaymentStateTranslation(status, t).text as string,
			),
		[facetsCounts.statuses, t],
	);

	const config = useMemo<FilterConfig<SchemaType>>(() => {
		const fields: FieldConfig<SchemaType>[] = [
			{
				key: "username",
				type: "search",
				placeholder: t("payment.username"),
			},
			{
				key: "statuses",
				type: "select",
				placeholder: t("payment.status"),
				options: statusOptions,
				searchable: true,
			},
		];

		return {
			fields,
			searchDebounceMs: 0,
			showClearButton: true,
		};
	}, [t, statusOptions]);

	const handleClearFilters = () => {
		clearFilters({
			payment_type: "",
			username: "",
			statuses: "",
		});
	};

	return (
		<div className="flex flex-col sm:flex-row gap-4">
			<GenericFilters
				config={config}
				filters={localFilters}
				onFiltersChange={onFiltersChange}
				onClearFilters={handleClearFilters}
			/>
		</div>
	);
}

export default PaymentReceivedSearch;
